import React, { useState, useRef } from 'react';
import { Send, Loader2, ChevronDown, Upload, X, Image, Type, Plus, FileText, Sparkles, ArrowRight, RotateCcw } from 'lucide-react';
import { useAuth, api } from '../context/AuthContext';
import { FeedbackResult } from './FeedbackDisplay';
import { FeedbackSkeleton } from './FeedbackSkeleton';
import AssignToClassModal from './AssignToClassModal';
import { useSubjects } from '../hooks/useSubjects';
import { LEVELS, getSubjectIcon } from '../lib/subjects';

const MARK_OPTIONS = [4, 6, 8, 10, 12, 14, 20];

const VALID_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function validateAndReadFile(file, onResult) {
    if (!VALID_FILE_TYPES.includes(file.type)) {
        alert('Please upload a JPEG, PNG, WebP image or PDF');
        return;
    }
    if (file.size > MAX_FILE_SIZE) {
        alert('File must be less than 10MB');
        return;
    }
    const reader = new FileReader();
    reader.onload = (e) => onResult(e.target.result);
    reader.readAsDataURL(file);
}

export default function CreateTab() {
    const { user } = useAuth();
    const { subjects: SUBJECTS } = useSubjects();
    const isTeacher = user?.role === 'teacher';
    // Step: 'question' -> 'markscheme' -> 'answer' -> 'result'
    const [step, setStep] = useState('question');
    const [subject, setSubject] = useState('business');
    const [level, setLevel] = useState('IAL');
    const [marks, setMarks] = useState(10);
    const [question, setQuestion] = useState('');
    const [questionImage, setQuestionImage] = useState(null);
    const [questionMode, setQuestionMode] = useState('text');
    const [markScheme, setMarkScheme] = useState(null);
    const [markBreakdown, setMarkBreakdown] = useState('');
    const [examinerNotes, setExaminerNotes] = useState('');
    const [studentAnswer, setStudentAnswer] = useState('');
    const [answerImages, setAnswerImages] = useState([]);
    const [answerMode, setAnswerMode] = useState('text');
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [subjectOpen, setSubjectOpen] = useState(false);
    const qFileRef = useRef(null);
    const aFileRef = useRef(null);
    const [showAssign, setShowAssign] = useState(false);

    const selectedSubject = SUBJECTS.find(s => s.id === subject);

    const handleGenerateMarkScheme = async () => {
        setError('');
        setLoading(true);
        try {
            const data = {
                subject: selectedSubject?.name || subject,
                level,
                question: question || '[See image]',
                marks
            };
            if (questionImage) data.question_image_base64 = questionImage;
            const res = await api.post('/api/create/generate-markscheme', data);
            setMarkScheme(res.data.mark_scheme);
            setMarkBreakdown(res.data.mark_breakdown || '');
            setExaminerNotes(res.data.examiner_notes || '');
            setStep('markscheme');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to generate mark scheme.');
        } finally {
            setLoading(false);
        }
    };

    const handleGrade = async () => {
        setError('');
        setLoading(true);
        try {
            const data = {
                subject: selectedSubject?.name || subject,
                level,
                question: question || '[See image]',
                mark_scheme: markScheme,
                student_answer: studentAnswer || '[See image]',
                marks
            };
            if (questionImage) data.question_image_base64 = questionImage;
            if (answerImages.length > 0) data.answer_image_base64 = answerImages;
            const res = await api.post('/api/create/grade', data);
            setFeedback(res.data);
            setStep('result');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to grade answer.');
        } finally {
            setLoading(false);
        }
    };

    const resetAll = () => {
        setStep('question');
        setQuestion('');
        setQuestionImage(null);
        setQuestionMode('text');
        setMarkScheme(null);
        setMarkBreakdown('');
        setExaminerNotes('');
        setStudentAnswer('');
        setAnswerImages([]);
        setAnswerMode('text');
        setFeedback(null);
        setError('');
    };

    const handleQuestionUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        validateAndReadFile(file, (result) => setQuestionImage(result));
    };

    const handleAnswerUpload = (e) => {
        const files = Array.from(e.target.files || []);
        files.forEach((file) => {
            validateAndReadFile(file, (result) => {
                setAnswerImages((prev) => [...prev, result]);
            });
        });
        if (aFileRef.current) aFileRef.current.value = '';
    };

    const isPdf = (data) => data?.startsWith('data:application/pdf');

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Input Area */}
            <div className="brutalist-card p-8" data-testid="create-form-card">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">Create & Mark</h2>
                    {step !== 'question' && (
                        <button onClick={resetAll} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-950 underline" data-testid="create-reset-button">
                            <RotateCcw className="w-3.5 h-3.5" /> Start over
                        </button>
                    )}
                </div>

                {/* Progress steps */}
                <div className="flex items-center gap-2 mb-6">
                    {['question', 'markscheme', 'answer', 'result'].map((s, i) => (
                        <React.Fragment key={s}>
                            <div className={`w-8 h-8 rounded-full border-2 border-zinc-950 flex items-center justify-center text-xs font-bold transition-colors ${
                                step === s ? 'bg-[#A78BFA] text-white' : 
                                ['question','markscheme','answer','result'].indexOf(step) > i ? 'bg-[#EDE9FE] text-zinc-700' : 'bg-white text-zinc-400'
                            }`}>{i + 1}</div>
                            {i < 3 && <div className={`flex-1 h-0.5 ${['question','markscheme','answer','result'].indexOf(step) > i ? 'bg-[#A78BFA]' : 'bg-zinc-200'}`} />}
                        </React.Fragment>
                    ))}
                </div>

                {error && (
                    <div className="p-3 bg-red-50 border-2 border-red-500 rounded-lg mb-4" data-testid="create-error">
                        <p className="text-red-700 text-sm font-medium">{error}</p>
                    </div>
                )}

                {/* Step 1: Write/upload question */}
                {step === 'question' && (
                    <div className="space-y-5 fade-in">
                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">Subject</label>
                            <div className="relative">
                                <button type="button" onClick={() => setSubjectOpen(!subjectOpen)} className="brutalist-input flex items-center justify-between w-full" data-testid="create-subject-selector">
                                    <span className="flex items-center gap-2">
                                        {selectedSubject && React.createElement(getSubjectIcon(selectedSubject.icon), { className: 'w-4 h-4 text-[#7C3AED]' })}
                                        <span>{selectedSubject?.name || 'Select subject'}</span>
                                    </span>
                                    <ChevronDown className={`w-5 h-5 transition-transform ${subjectOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {subjectOpen && (
                                    <div className="absolute z-10 w-full mt-2 bg-white border-2 border-zinc-950 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                                        {SUBJECTS.map((s) => {
                                            const Icon = getSubjectIcon(s.icon);
                                            return (
                                                <button key={s.id} type="button" onClick={() => { setSubject(s.id); setSubjectOpen(false); }}
                                                    className={`w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-zinc-50 ${subject === s.id ? 'bg-[#EDE9FE]' : ''}`}>
                                                    <Icon className="w-4 h-4 text-[#7C3AED]" /><span className="font-medium">{s.name}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">Level</label>
                            <div className="inline-flex bg-zinc-100 p-1 rounded-full" data-testid="create-level-selector">
                                {LEVELS.map((l) => (
                                    <button key={l.id} type="button" onClick={() => setLevel(l.id)}
                                        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                                            level === l.id ? 'bg-white text-[#7C3AED] shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                                        }`}
                                        data-testid={`create-level-${l.id}`}>
                                        {l.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">Total Marks</label>
                            <div className="flex flex-wrap gap-2">
                                {MARK_OPTIONS.map((m) => (
                                    <button key={m} type="button" onClick={() => setMarks(m)}
                                        className={`px-4 py-2 rounded-lg border-2 text-sm font-bold transition-all ${marks === m ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}
                                        data-testid={`create-marks-${m}`}>
                                        {m}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">Your Question</label>
                            <div className="flex gap-2 mb-3">
                                <button type="button" onClick={() => setQuestionMode('text')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm ${questionMode === 'text' ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}>
                                    <Type className="w-4 h-4" /> Type
                                </button>
                                <button type="button" onClick={() => setQuestionMode('image')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm ${questionMode === 'image' ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}>
                                    <Image className="w-4 h-4" /> Upload
                                </button>
                            </div>
                            {questionMode === 'text' ? (
                                <textarea value={question} onChange={(e) => setQuestion(e.target.value)} className="brutalist-textarea" placeholder="Write your custom exam question..." data-testid="create-question-input" />
                            ) : (
                                <div className="space-y-2">
                                    {questionImage ? (
                                        <div className="relative inline-block">
                                            {isPdf(questionImage) ? (
                                                <div className="flex items-center gap-3 p-4 rounded-lg border-2 border-zinc-950 bg-zinc-50">
                                                    <FileText className="w-8 h-8 text-[#A78BFA]" /><span className="text-sm font-medium">PDF uploaded</span>
                                                </div>
                                            ) : (
                                                <img src={questionImage} alt="Question" className="max-h-48 object-contain rounded-lg border-2 border-zinc-950" />
                                            )}
                                            <button type="button" onClick={() => { setQuestionImage(null); if (qFileRef.current) qFileRef.current.value = ''; }} className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-zinc-400 rounded-lg cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-all">
                                            <Upload className="w-7 h-7 text-zinc-400 mb-2" />
                                            <span className="text-zinc-600 font-medium text-sm">Upload question</span>
                                            <span className="text-zinc-400 text-xs mt-1">JPEG, PNG, WebP or PDF</span>
                                            <input ref={qFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleQuestionUpload} className="hidden" />
                                        </label>
                                    )}
                                </div>
                            )}
                        </div>

                        <button onClick={handleGenerateMarkScheme} disabled={loading || (!question && !questionImage)}
                            className="brutalist-btn-primary w-full flex items-center justify-center gap-2" data-testid="create-generate-ms-button">
                            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Generating mark scheme...</>) : (<><Sparkles className="w-5 h-5" /> Generate Mark Scheme</>)}
                        </button>
                    </div>
                )}

                {/* Step 2: Review mark scheme */}
                {step === 'markscheme' && (
                    <div className="space-y-5 fade-in">
                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">AI-Generated Mark Scheme ({marks} marks)</label>
                            {markBreakdown && <p className="text-xs text-[#A78BFA] font-semibold mb-2">{markBreakdown}</p>}
                            <div className="bg-[#EDE9FE] border-2 border-zinc-950 rounded-lg p-4 text-sm whitespace-pre-wrap" data-testid="create-markscheme-display">{markScheme}</div>
                        </div>
                        {examinerNotes && (
                            <div className="bg-zinc-50 border-2 border-zinc-300 rounded-lg p-3">
                                <p className="text-xs font-semibold text-zinc-500 mb-1">Examiner Notes</p>
                                <p className="text-sm text-zinc-700">{examinerNotes}</p>
                            </div>
                        )}
                        <button onClick={() => setStep('answer')} className="brutalist-btn-primary w-full flex items-center justify-center gap-2" data-testid="create-proceed-answer-button">
                            <ArrowRight className="w-5 h-5" /> Write Your Answer
                        </button>
                        {isTeacher && (
                            <button
                                type="button"
                                onClick={() => setShowAssign(true)}
                                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white border-2 border-[#7C3AED] text-[#7C3AED] font-bold text-sm hover:bg-[#F4ECFF] transition-colors"
                                data-testid="create-assign-to-class"
                            >
                                <Send className="w-4 h-4" /> Assign this question to a class
                            </button>
                        )}
                        {showAssign && (
                            <AssignToClassModal
                                source="create"
                                subject={selectedSubject?.name || subject}
                                level={level}
                                title={`${selectedSubject?.name || subject} · ${marks} marks · ${(question || 'Image question').slice(0, 60)}`}
                                question_text={question}
                                mark_scheme={markScheme}
                                marks={marks}
                                onClose={() => setShowAssign(false)}
                            />
                        )}
                    </div>
                )}

                {/* Step 3: Write/upload answer */}
                {step === 'answer' && (
                    <div className="space-y-5 fade-in">
                        <div className="bg-zinc-50 border-2 border-zinc-300 rounded-lg p-3">
                            <p className="text-xs font-semibold text-zinc-500 mb-1">Question</p>
                            <p className="text-sm text-zinc-700">{question || '[Image question]'}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">Your Answer</label>
                            <div className="flex gap-2 mb-3">
                                <button type="button" onClick={() => setAnswerMode('text')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm ${answerMode === 'text' ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}>
                                    <Type className="w-4 h-4" /> Type
                                </button>
                                <button type="button" onClick={() => setAnswerMode('image')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm ${answerMode === 'image' ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}>
                                    <Image className="w-4 h-4" /> Upload
                                </button>
                            </div>
                            {answerMode === 'text' ? (
                                <textarea value={studentAnswer} onChange={(e) => setStudentAnswer(e.target.value)} className="brutalist-textarea" style={{ minHeight: '180px' }} placeholder="Write your answer..." data-testid="create-answer-input" />
                            ) : (
                                <div className="space-y-3">
                                    {answerImages.length > 0 && (
                                        <div className="flex flex-wrap gap-3">
                                            {answerImages.map((img, i) => (
                                                <div key={i} className="relative group">
                                                    {isPdf(img) ? (
                                                        <div className="h-28 flex items-center gap-2 p-3 rounded-lg border-2 border-zinc-950 bg-zinc-50">
                                                            <FileText className="w-6 h-6 text-[#A78BFA]" /><span className="text-xs font-medium">PDF {i + 1}</span>
                                                        </div>
                                                    ) : (
                                                        <img src={img} alt={`Answer ${i + 1}`} className="h-28 w-auto object-contain rounded-lg border-2 border-zinc-950" />
                                                    )}
                                                    <button type="button" onClick={() => setAnswerImages(prev => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                    <span className="absolute bottom-1 left-1 bg-zinc-950 text-white text-xs px-1.5 py-0.5 rounded">{i + 1}</span>
                                                </div>
                                            ))}
                                            <label className="h-28 w-20 flex flex-col items-center justify-center border-2 border-dashed border-zinc-400 rounded-lg cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-all">
                                                <Plus className="w-5 h-5 text-zinc-400" /><span className="text-zinc-400 text-xs">Add</span>
                                                <input ref={aFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple onChange={handleAnswerUpload} className="hidden" />
                                            </label>
                                        </div>
                                    )}
                                    {answerImages.length === 0 && (
                                        <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-zinc-400 rounded-lg cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-all">
                                            <Upload className="w-7 h-7 text-zinc-400 mb-2" />
                                            <span className="text-zinc-600 font-medium text-sm">Upload your answer</span>
                                            <span className="text-zinc-400 text-xs mt-1">JPEG, PNG, WebP or PDF — multiple allowed</span>
                                            <input ref={aFileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple onChange={handleAnswerUpload} className="hidden" />
                                        </label>
                                    )}
                                </div>
                            )}
                        </div>

                        <button onClick={handleGrade} disabled={loading || (!studentAnswer && answerImages.length === 0)}
                            className="brutalist-btn-primary w-full flex items-center justify-center gap-2" data-testid="create-grade-button">
                            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Grading your answer...</>) : (<><Send className="w-5 h-5" /> Get AI Feedback</>)}
                        </button>
                    </div>
                )}

                {/* Step 4: Result - show start over */}
                {step === 'result' && (
                    <div className="space-y-5 fade-in">
                        <div className="bg-zinc-50 border-2 border-zinc-300 rounded-lg p-3">
                            <p className="text-xs font-semibold text-zinc-500 mb-1">Question</p>
                            <p className="text-sm text-zinc-700">{question || '[Image question]'}</p>
                        </div>
                        <div className="bg-[#EDE9FE] border-2 border-zinc-950 rounded-lg p-3">
                            <p className="text-xs font-semibold text-[#A78BFA] mb-1">AI Mark Scheme</p>
                            <p className="text-sm text-zinc-700 whitespace-pre-wrap">{markScheme}</p>
                        </div>
                        <button onClick={resetAll} className="brutalist-btn-secondary w-full flex items-center justify-center gap-2" data-testid="create-new-button">
                            <RotateCcw className="w-5 h-5" /> Create Another Question
                        </button>
                    </div>
                )}
            </div>

            {/* Right: Feedback/Skeleton */}
            <div>
                {loading && step === 'answer' ? (
                    <FeedbackSkeleton />
                ) : feedback ? (
                    <FeedbackResult feedback={feedback} />
                ) : (
                    <div className="brutalist-card p-8" data-testid="create-placeholder">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-[#EDE9FE] flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-8 h-8 text-[#A78BFA]" />
                            </div>
                            <h3 className="text-xl font-bold mb-2">Create & Mark</h3>
                            <p className="text-zinc-500 text-sm">
                                {step === 'question' && "Write a custom question, and the AI will generate a mark scheme and grade your answer."}
                                {step === 'markscheme' && "Review the AI-generated mark scheme, then write your answer."}
                                {step === 'answer' && "Submit your answer to get graded against the AI mark scheme."}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
